import { $Props } from '@Ravnur-Inc/core/typings/tsx';
import Scrollable from '@Ravnur-Inc/core/ui-kit/scrollable/scrollable';
import { Debounce, Watch, Prop } from '@Ravnur-Inc/decorators';
import { keyById } from '@Ravnur-Inc/nanoutils/keyBy';
import { showErrorNotification } from '@Ravnur-Inc/notifications/service';
import { renderSlot } from 'vue';
import { setup, Vue } from 'vue-class-component';

import MediaGridSnippet from '@/components/media/media-grid-snippet/media-grid-snippet';
import MediaRepository, { MediaQueryParams } from '@/repositories/media-repository';
import useSecurityStore from '@/store/security';
import { Media, Media$Availability, Media$Privacy, Media$Type } from '@/types/Media';
import { sort2RequestParams } from '@/transformers/sort-by';

import './media-picker-modal.scss';

type Props = {
  value: Media[];
  excluded?: Media[];
  maxCount?: number;
  showTypes?: boolean;
  queryParams?: Partial<MediaQueryParams>;
  privacy?: Media$Privacy[];
  process: (media: Media[]) => Promise<void>;
  close?: () => void;
};

const CN = 'media-picker-modal';
const PAGE_SIZE = 12;
const NEXT_PAGE_ZONE = 250;
const repository = new MediaRepository();

export default class MediaPickerModal extends Vue {
  declare $props: $Props<Props, unknown, 'value'>;
  declare $refs: {
    content: Scrollable | undefined;
  };

  @Prop({}) value: Media[];
  @Prop({ default: [] }) excluded: Media[];
  @Prop({ default: 0 }) maxCount: number;
  @Prop({ default: false }) showTypes: boolean;
  @Prop({ default: {} }) queryParams: Partial<MediaQueryParams>;
  @Prop({ default: [] }) privacy: Media$Privacy[];
  @Prop({}) process: (media: Media[]) => Promise<void>;
  @Prop({}) close: () => void;

  private security = setup(() => useSecurityStore());

  private selected: Media[] = [];
  private items: Media[] = [];
  private onlyMyContent = false;
  private q = '';
  private page = 0;
  private loading = false;
  private hasMore = true;
  private processing = false;
  private mediaType: Media$Type | '' = '';

  get isExcess() {
    const { selected, excluded, maxCount } = this;
    return maxCount && maxCount <= selected.length + excluded.length;
  }

  get media4ignoring() {
    return keyById(this.excluded.concat(this.selected));
  }

  get candidates(): Media[] {
    const { media4ignoring, items } = this;
    return items.filter(({ id }) => !media4ignoring[id]);
  }

  get currentUser() {
    return this.security.user;
  }

  get types() {
    return [
      { id: '', label: this.$t('common', 'media-picker__all-types') },
      { id: Media$Type.AUDIO, label: this.$t('common', 'menu__audio') },
      { id: Media$Type.VIDEO, label: this.$t('common', 'menu__video') },
      { id: Media$Type.PLAYLIST, label: this.$t('common', 'menu__playlists') },
    ];
  }

  @Watch('value', { immediate: true })
  protected handleValueChanged() {
    this.selected = [...this.value];
  }

  created() {
    this.loadFirstPage();
  }

  private async confirm() {
    this.processing = true;
    try {
      await this.process(this.selected);
      this.$emit('input', this.selected);
      this.$nextTick(this.close);
    } catch (e) {
      this.$processException(e);
    }

    this.processing = false;
  }

  render() {
    return (
      <popup
        canConfirm={!!this.selected.length}
        class={CN}
        closeLabel="Cancel"
        processing={this.processing}
        onClose={this.close}
        onConfirm={this.confirm}
      >
        <div class={`${CN}__container`}>
          <div class={`${CN}__header`}>
            <div class={`${CN}__col`}>
              <div class={`${CN}__filter`}>
                {renderSlot(this.$slots, 'filters')}
                <checkbox
                  label={this.$t('common', 'media-picker__my-content')}
                  vModel={[this.onlyMyContent, 'value']}
                />
                <chosen
                  options={this.types}
                  v-show={this.showTypes}
                  vModel={[this.mediaType, 'value']}
                />
                <text-field icon="search" vModel={[this.q, 'value']} />
              </div>
            </div>
            <div class={`${CN}__col ${CN}__col--selected-title`}>
              <l10n class={`${CN}__selected-title`} group="common" tkey="media-picker__selected" />
            </div>
          </div>
          <div class={`${CN}__items-wrapper`}>
            <div class={`${CN}__col`}>{this.renderCandidates()}</div>
            <div class={`${CN}__col`}>{this.renderSelected()}</div>
          </div>
          {this.loading ? <spinner /> : null}
        </div>
      </popup>
    );
  }

  private renderCandidates() {
    return (
      <scrollable
        ref="content"
        class={`${CN}__candidates`}
        v-slots={{ default: this.renderCandidatesMedia }}
        onScroll={this.loadNextPageIfNeeded}
      />
    );
  }

  private renderCandidatesMedia() {
    return (
      <>
        {this.candidates.map(this.createMediaGridRenderer(this.select))}
        {this.renderEmptyMessage()}
      </>
    );
  }

  private renderEmptyMessage() {
    if (this.loading || this.candidates.length) {
      return;
    }
    return (
      <alert class={`${CN}__warn`} mode="info">
        <l10n group="common" tkey="media-picker__empty-msg" />
      </alert>
    );
  }

  renderSelected() {
    return <scrollable class={`${CN}__selected`} v-slots={{ default: this.renderSelectedMedia }} />;
  }

  renderSelectedMedia() {
    return this.selected.map(this.createMediaGridRenderer(this.unselect));
  }

  createMediaGridRenderer(listener: (media: Media) => void) {
    return (media: Media) => (
      <MediaGridSnippet key={media.id} media={media} onclick={() => listener(media)} />
    );
  }

  @Watch('q')
  @Debounce(+process.env.VUE_APP_SEARCH_DEBOUNCE_TIME)
  protected loadFirstPageDelayed() {
    this.loadFirstPage();
  }

  @Watch('mediaType')
  @Watch('onlyMyContent')
  private loadFirstPage() {
    this.page = 0;
    this.items.length = 0;
    this.scrollToTop();
    this.loadPart();
  }

  private scrollToTop() {
    this.$refs.content?.setScrollTop(0);
  }

  private loadNextPageIfNeeded() {
    if (!this.hasMore || this.loading) {
      return;
    }

    const content = this.$refs.content;
    if (!content) {
      return;
    }
    const clientHeight = content.getClientHeight();
    const scrollHeight = content.getScrollHeight();
    const st = content.getScrollTop();

    if (st + clientHeight + NEXT_PAGE_ZONE >= scrollHeight) {
      this.page += 1;
      this.loadPart();
    }
  }

  private async loadPart() {
    this.loading = true;
    const { sortType, sortField } = sort2RequestParams('-CreatedDate');

    const params: Partial<MediaQueryParams> = {
      ...this.queryParams,
      count: PAGE_SIZE,
      offset: PAGE_SIZE * this.page,
      privacy: this.privacy,
      availability: Media$Availability.AVAILABLE,
      sortType,
      sortField: sortField as keyof Media,
    };

    if (this.q) {
      params.q = this.q;
    }

    if (this.onlyMyContent && this.currentUser) {
      params.owner = this.currentUser.id;
    }

    if (this.showTypes && this.mediaType) {
      params.type = this.mediaType;
    }

    try {
      const { items, total } = await repository.load(params);
      this.items = this.items.concat(items);
      this.hasMore = total > this.items.length;
    } catch (e) {
      this.$processException(e);
    }
    this.loading = false;

    setTimeout(this.loadNextPageIfNeeded, 0);
  }

  private select(media: Media) {
    if (this.isExcess) {
      const { maxCount: total } = this;
      const msg = this.$t('common', 'media-picker__full-list-message', { total }, total);
      showErrorNotification(msg);
    } else {
      this.selected.push(media);
    }
  }

  private unselect(media: Media) {
    this.selected = this.selected.filter(({ id }) => id !== media.id);
  }
}
